import React, { Component, Fragment } from 'react';
import moment from 'moment';
import { Breakpoint, ButtonTheme, Key, ModalType } from '@philips-internal/oswald-ui-constants';
import { AbortableNavigateEvent, ChangeEvent, FocusEvent, NavigateEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { getPrettyDateValue } from '@philips-internal/oswald-ui-model';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils.js';
import { classNames, getBreakpoint, isFunction, isTouchableDevice, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as CalendarIcon } from '../../../resources/assets/components/calendar.svg';
import { ReactComponent as CloseIcon } from '../../../resources/assets/components/DLS_CloseCrossCircle.svg';
import { Button } from '../button/button.js';
import { DatePicker } from '../date-picker/date-picker.js';
import { FieldContext } from '../field/context/field-context.js';
import { Modal } from '../modal/modal.js';
import { Popover } from '../popover/popover.js';
import { SlideUpPanel } from '../slide-up-panel/slide-up-panel.js';
import { DateFieldBasic } from './components/date-field-basic.js';
import { DateFieldInterval } from './components/date-field-interval.js';
import { DateFieldProperties } from './date-field-types.js';
import './date-field.css';
function parseDate(value) {
    return moment.utc(value).format(ISO_DATE_FORMAT);
}
function parseDates(values) {
    return values.map(parseDate);
}
function parseValue(value) {
    if (Array.isArray(value)) {
        return parseDates(value);
    }
    return parseDate(value);
}
const shallowCompareState = [
    'showModal',
    'tempValue',
];
const shallowCompareProps = [
    'data',
    'enabled',
    'initialDate',
    'interval',
    'intervalThreshold',
    'placeholder',
    'ranges',
    'rangeState',
    'readOnly',
    'sensitiveData',
    'strikethrough',
    'uuid',
    'valid',
    'value',
];
class DateField extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            showModal: false,
        };
        this.onKeyDownCloseModal = (event) => {
            if (event.key === Key.ESCAPE) {
                this.closeModal();
            }
        };
        this.onChange = (event) => {
            this.fireChange(event.value);
        };
        this.onDatePickerChange = (event) => {
            const { value } = event;
            if (isTouchableDevice()) {
                this.setState({
                    tempValue: [value].flat(),
                });
            }
            else {
                this.closeModal();
                this.fireChange(value ? parseValue(value) : value);
            }
        };
        this.onAfterNavigate = (event) => {
            const { onAfterNavigate } = this.props;
            const { from, to } = event;
            if (isFunction(onAfterNavigate)) {
                onAfterNavigate(new NavigateEvent({
                    from,
                    to,
                }));
            }
        };
        this.onNavigate = (event) => {
            const { onNavigate } = this.props;
            const { abort, from, to } = event;
            if (isFunction(onNavigate)) {
                onNavigate(new AbortableNavigateEvent({
                    abort,
                    from,
                    to,
                }));
            }
        };
        this.onBlur = () => {
            const { onBlur } = this.props;
            if (isFunction(onBlur)) {
                onBlur(new FocusEvent());
            }
        };
        this.applyValue = () => {
            this.closeModal();
            this.fireChange(this.getModalValue());
        };
        this.clearValue = () => {
            this.setState({ tempValue: [] });
        };
        this.selectToday = () => {
            this.setState({ tempValue: [moment().utc().toISOString()] });
        };
        this.closeModal = () => {
            this.setState({
                showModal: false,
                tempValue: undefined,
            });
        };
        this.openModal = () => {
            this.setState({ showModal: true });
        };
        this.getIntervalValue = () => {
            const { value } = this.props;
            return [value].flat();
        };
        this.getValue = () => {
            const { value } = this.props;
            return Array.isArray(value) ? value[0] : value;
        };
        this.renderDatePicker = (interval, externalControls = false) => {
            const { applyExpression = DateFieldProperties.applyExpression.defaultValue, clearExpression = DateFieldProperties.clearExpression.defaultValue, data, initialDate, intervalThreshold = DateFieldProperties.intervalThreshold.defaultValue, rangeState = DateFieldProperties.rangeState.defaultValue, noAppointmentsExpression, ranges = DateFieldProperties.ranges.defaultValue, todayExpression = DateFieldProperties.todayExpression.defaultValue, } = this.props;
            const expressions = {
                applyExpression,
                clearExpression,
                noAppointmentsExpression,
                todayExpression,
            };
            return (React.createElement(DatePicker, { ...expressions, data: data, externalControls: externalControls, initialDate: initialDate, interval: interval, intervalThreshold: intervalThreshold, isModalWrapped: true, onAfterNavigate: this.onAfterNavigate, onChange: this.onDatePickerChange, onNavigate: this.onNavigate, rangeState: rangeState, ranges: ranges, value: this.getModalValue() }));
        };
        this.onKeyDownOpenModal = (event) => {
            if (event.key === Key.ENTER) {
                this.openModal();
            }
        };
        this.clearInput = () => {
            this.fireChange('');
        };
        this.onKeyDownClearInput = (event) => {
            if (event.key === Key.ENTER) {
                this.clearInput();
            }
        };
    }
    /**
     * Prettifies a given value.
     *
     * @param properties - Properties to generate pretty value.
     */
    static formatPrettyValue(properties) {
        const { value } = properties;
        if (!Array.isArray(value)) {
            return getPrettyDateValue(value);
        }
        const [startDate, endDate] = value;
        return getPrettyDateValue([startDate, endDate]);
    }
    componentDidMount() {
        document.addEventListener('keydown', this.onKeyDownCloseModal);
    }
    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKeyDownCloseModal);
    }
    shouldComponentUpdate(nextProps, nextState) {
        if (shallowCompare(this.state, nextState, shallowCompareState)) {
            return true;
        }
        if (shallowCompare(this.props, nextProps, shallowCompareProps)) {
            return true;
        }
        return false;
    }
    updateValidations(value) {
        if (!this.context) {
            return;
        }
        const validationRunner = ModelService.validate(value);
        validationRunner.apply('date');
        this.context.updateChildValidations(validationRunner.run());
    }
    fireChange(value) {
        const { onChange, value: oldValue } = this.props;
        if (isFunction(onChange)) {
            this.updateValidations(value);
            onChange(new ChangeEvent({
                oldValue: oldValue,
                prettyValue: DateField.formatPrettyValue({ value }),
                value: value,
            }));
        }
    }
    getModalValue() {
        const { interval = DateFieldProperties.interval.defaultValue, value, } = this.props;
        const { tempValue } = this.state;
        const modalValue = tempValue ? parseValue(tempValue) : value;
        if (interval) {
            return modalValue ? [modalValue].flat() : [];
        }
        return Array.isArray(modalValue) ? modalValue[0] : modalValue;
    }
    getExternalActions() {
        const { enabled = DateFieldProperties.enabled.defaultValue, openCalendarLabel = DateFieldProperties.openCalendarLabel.defaultValue, readOnly, } = this.props;
        if (!enabled || readOnly) {
            return null;
        }
        if (isTouchableDevice()) {
            return (React.createElement("div", { "aria-label": MessageService.translate(openCalendarLabel), className: "c-date-field__calendar", onClick: this.openModal, onKeyDown: (event) => this.onKeyDownOpenModal(event), role: "button", tabIndex: 0 },
                React.createElement(CalendarIcon, null)));
        }
        return this.renderDesktopCalendar();
    }
    renderCloseIcon() {
        const { enabled = DateFieldProperties.enabled.defaultValue, value } = this.props;
        const isArray = Array.isArray(value);
        const isEmptyArray = isArray && !value.length;
        const allArrayItensInvalid = isArray && value.every(item => !item);
        if (!enabled || !value || isEmptyArray || allArrayItensInvalid) {
            return null;
        }
        return (React.createElement("div", { className: "c-date-field__clear", onClick: this.clearInput, onKeyDown: this.onKeyDownClearInput, role: "button", tabIndex: 0 },
            React.createElement(CloseIcon, null)));
    }
    renderDesktopCalendar() {
        const { interval = DateFieldProperties.interval.defaultValue, openCalendarLabel = DateFieldProperties.openCalendarLabel.defaultValue, } = this.props;
        const { showModal } = this.state;
        return (React.createElement(Popover, { onClose: this.closeModal, open: showModal },
            React.createElement("div", { className: "c-date-field__actions" },
                this.renderCloseIcon(),
                React.createElement("div", { "aria-label": MessageService.translate(openCalendarLabel), className: "c-date-field__calendar", onClick: this.openModal, onKeyDown: this.onKeyDownOpenModal, role: "button", tabIndex: 0 },
                    React.createElement(CalendarIcon, null))),
            React.createElement(Popover.Content, null, this.renderDatePicker(interval))));
    }
    getFormattedModalValue() {
        const modalValue = this.getModalValue();
        if (!modalValue || modalValue.length === 0) {
            return;
        }
        const selectedDate = Array.isArray(modalValue) ? modalValue[0] : modalValue;
        return ModelService.format('date', selectedDate, { type: 'fullDate' });
    }
    renderPanelHeaderContent() {
        const modalValue = this.getFormattedModalValue();
        if (!modalValue) {
            return React.createElement("div", { className: "c-date-field__modal__header__message" }, MessageService.translate('tws.date-field.select-date'));
        }
        return (React.createElement("div", { className: "c-date-field__modal__header__title" },
            React.createElement("div", { className: "c-date-field__modal__header__date" }, modalValue)));
    }
    renderSecondaryButtons() {
        const { clearExpression = DateFieldProperties.clearExpression.defaultValue, todayExpression = DateFieldProperties.todayExpression.defaultValue, } = this.props;
        const modalValue = this.getModalValue();
        if (modalValue?.length > 0) {
            return React.createElement(Button, { onClick: this.clearValue, theme: ButtonTheme.SECONDARY, value: clearExpression });
        }
        return React.createElement(Button, { onClick: this.selectToday, theme: ButtonTheme.SECONDARY, value: todayExpression });
    }
    renderMobileCalendar() {
        const { applyExpression = DateFieldProperties.applyExpression.defaultValue, interval = DateFieldProperties.interval.defaultValue, } = this.props;
        const { showModal } = this.state;
        const breakpoint = getBreakpoint();
        if (breakpoint === Breakpoint.XSM) {
            return (React.createElement(SlideUpPanel, { closeable: true, onClose: this.closeModal, open: showModal },
                React.createElement(SlideUpPanel.Header, null, this.renderPanelHeaderContent()),
                React.createElement(SlideUpPanel.Content, null,
                    React.createElement("div", { className: "c-date-field__modal__content" }, this.renderDatePicker(interval, true))),
                React.createElement(SlideUpPanel.Footer, null,
                    React.createElement("div", { className: "c-date-field__modal__footer" },
                        this.renderSecondaryButtons(),
                        React.createElement(Button, { onClick: this.applyValue, value: applyExpression })))));
        }
        if (!showModal) {
            return null;
        }
        const modalValue = this.getFormattedModalValue();
        return (React.createElement(Modal, { className: "c-date-field__modal", closeable: true, onClose: this.closeModal, title: modalValue ?? 'tws.date-field.select-date', type: ModalType.DEFAULT },
            React.createElement(Fragment, null,
                React.createElement("div", { className: "c-date-field__modal__content" }, this.renderDatePicker(interval, true)),
                React.createElement("div", { className: "c-date-field__modal__footer" },
                    this.renderSecondaryButtons(),
                    React.createElement(Button, { onClick: this.applyValue, value: applyExpression })))));
    }
    renderCalendar() {
        const { readOnly } = this.props;
        if (readOnly || !isTouchableDevice()) {
            return null;
        }
        return this.renderMobileCalendar();
    }
    renderDateField() {
        const { enabled = DateFieldProperties.enabled.defaultValue, interval = DateFieldProperties.interval.defaultValue, placeholder, readOnly, valid = DateFieldProperties.valid.defaultValue, sensitiveData, } = this.props;
        if (interval) {
            const [startDate, endDate] = this.getIntervalValue();
            return (React.createElement(DateFieldInterval, { enabled: enabled, endDate: endDate, externalAction: this.getExternalActions(), onClick: this.openModal, placeholder: placeholder, readOnly: readOnly, sensitiveData: sensitiveData, startDate: startDate, valid: valid }));
        }
        return (React.createElement(DateFieldBasic, { date: this.getValue(), enabled: enabled, externalAction: this.getExternalActions(), onBlur: this.onBlur, onChange: this.onChange, placeholder: placeholder, readOnly: readOnly, sensitiveData: sensitiveData, valid: valid }));
    }
    render() {
        const { strikethrough } = this.props;
        const fieldClassName = classNames({
            'c-date-field': true,
            'u-text-strikethrough': Boolean(strikethrough),
        });
        return (React.createElement("div", { className: fieldClassName },
            React.createElement("div", { className: "c-date-field__container" }, this.renderDateField()),
            this.renderCalendar()));
    }
}
DateField.contextType = FieldContext;
export { DateField, };
//# sourceMappingURL=date-field.js.map